import type React from "react";
import { memo } from "react";

import styled, { css, keyframes } from "styled-components";

import B2bTimerLogo from "assets/logo-break-b2b.svg";
import { ReactComponent as B2cTimerComponent } from "assets/logo-break-b2c.svg";
import buildB2bLogo from "components/b2b/Logo";
import { SCOPE } from "shared-types/constants";
import { B2bUserType } from "shared-types/user";
import type { Mp3KbpPlayerCommonColors } from "stylesheet";
import { colorFilterUsage } from "stylesheetB2b";
import { getDefaultLogo } from "types/logo/logo";

export const FADE_OUT_DURATION_IN_S = 0.5;
export const CIRCULAR_PROGRESS_SIZE_IN_PERCENT = 92;
const CIRCULAR_PROGRESS_POSITIONING_OFFSET_IN_PERCENT =
	(100 - CIRCULAR_PROGRESS_SIZE_IN_PERCENT) / 2;
export const SIZING_FACTORS = { markerSize: 1 / 35 };
const TIMER_CONTAINER_Z_INDEX = 1;
const TIMER_LOGO_Z_INDEX = TIMER_CONTAINER_Z_INDEX + 1;
const TIMER_PROGRESS_CONTAINER_Z_INDEX = TIMER_LOGO_Z_INDEX + 1;
const TIMER_PROGRESS_MARKER_CONTAINER_Z_INDEX =
	TIMER_PROGRESS_CONTAINER_Z_INDEX + 1;
const TIMER_PROGRESS_MARKER_Z_INDEX =
	TIMER_PROGRESS_MARKER_CONTAINER_Z_INDEX + 1;

interface TimerContainerProps {
	isVisible: boolean;
	isBam: boolean;
}

export const TimerContainer = styled.div<TimerContainerProps>`
  position: absolute;
  top: ${({ isBam }) => (isBam ? "100%" : "50%")};
  left: 50%;
  width: fit-content;
  height: ${({ isBam }) => (isBam ? "60%" : "37%")};
  transform: translate(-50%, -50%);
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity ${FADE_OUT_DURATION_IN_S}s;
  z-index: ${TIMER_CONTAINER_Z_INDEX};
`;
TimerContainer.displayName = "TimerContainer";

interface TimerProps {
	scope: SCOPE;
	className?: string;
	userType: B2bUserType;
}

const LogoWraper: React.FC<TimerProps> = ({ scope, className }) => {
	const TSL_LOGO = buildB2bLogo(getDefaultLogo(B2bTimerLogo));

	if (className) {
		const TimerComponent = scope === SCOPE.B2B ? TSL_LOGO : B2cTimerComponent;
		return <TimerComponent className={className} />;
	}

	return <img alt="tsl logo" />;
};

interface StyledTimerLogoProps {
	scope: SCOPE;
	userType: B2bUserType;
}

export const StyledTimerLogo = styled(memo(LogoWraper))<StyledTimerLogoProps>`
  height: 100%;
  filter: ${({ scope, userType }) =>
		scope === SCOPE.B2B && userType === B2bUserType.TSL
			? colorFilterUsage.tslLogoInverted
			: "none"};
  z-index: ${TIMER_LOGO_Z_INDEX};
`;

export const CircularProgressContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${TIMER_PROGRESS_CONTAINER_Z_INDEX};
`;
CircularProgressContainer.displayName = "CircularProgressContainer";

const markerSpinAnimation = keyframes`
 0% {
  transform: translate(-50%) rotate(0turn)
 }
 100% {
  transform: translate(-50%) rotate(1turn)
 }
`;

interface ProgressMarkerContainerProps {
	markerSize: number;
	timerDurationInMs: number;
	isPlaying: boolean;
	delayInMilliseconds: number;
}

export const ProgressMarkerContainer = styled.div<ProgressMarkerContainerProps>`
  ${({ markerSize, timerDurationInMs, isPlaying, delayInMilliseconds }) => css`
    position: absolute;
    height: ${CIRCULAR_PROGRESS_SIZE_IN_PERCENT}%;
    top: ${CIRCULAR_PROGRESS_POSITIONING_OFFSET_IN_PERCENT}%;
    left: 50%;
    width: ${markerSize}px;
    animation: ${markerSpinAnimation} ${timerDurationInMs}ms linear ${delayInMilliseconds}ms
      forwards;
    animation-play-state: ${isPlaying ? "running" : "paused"};
    z-index: ${TIMER_PROGRESS_MARKER_CONTAINER_Z_INDEX};
  `}
`;
ProgressMarkerContainer.displayName = "ProgressMarkerContainer";

interface ProgressMarkerProps {
	markerSize: number;
	mp3KbpPlayerColors: Mp3KbpPlayerCommonColors;
}

export const ProgressMarker = styled.div<ProgressMarkerProps>`
  ${({ markerSize, mp3KbpPlayerColors }) => {
		return css`
      position: absolute;
      top: ${-markerSize / 2}px;
      height: ${markerSize}px;
      width: ${markerSize}px;
      border-radius: 50%;
      background-color: ${mp3KbpPlayerColors.timerCircularProgress};
      z-index: ${TIMER_PROGRESS_MARKER_Z_INDEX};
    `;
	}}
`;
ProgressMarker.displayName = "ProgressMarker";
